import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, Divider, Chip, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, Alert, TextField, InputAdornment, Button
} from '@mui/material';
import { 
  AttachMoney, AccountBalance, TrendingUp, TrendingDown,
  Warning, CheckCircle
} from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import axiosInstance from '../utils/axiosConfig';
import { GroupBudgetData, GroupBudgetLine } from '../types/groupBudget';
import { IMC_INCOME_CODES, IMC_EXPENSE_CODES, getIMCCodeByCode } from '../constants/imcCodes';
import { getFrequencyMultiplier } from '../utils/frequencyUtils';

interface BudgetVsActualDashboardProps {
  budget: GroupBudgetData;
  currentMonth?: number; // 1-12 for Jan-Dec
}

interface ActualData {
  code: string;
  description: string;
  actual: number;
}

interface CashflowState {
  currentBankBalance: number;
  projectedEndOfMonth: number;
  projectedEndOfYear: number;
}

interface BudgetVsActualSummary {
  budgetTotal: number;
  actualTotal: number;
  variance: number;
  percentageSpent: number;
  forecastTotal: number;
  forecastVariance: number;
}

const BudgetVsActualDashboard: React.FC<BudgetVsActualDashboardProps> = ({ 
  budget,
  currentMonth = new Date().getMonth() + 1 // Default to current month
}) => {
  const [actualsData, setActualsData] = useState<ActualData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [cashflow, setCashflow] = useState<CashflowState>({
    currentBankBalance: 0,
    projectedEndOfMonth: 0,
    projectedEndOfYear: 0
  });
  const [bankBalanceInput, setBankBalanceInput] = useState<string>('');
  const [unbudgetedExpenses, setUnbudgetedExpenses] = useState<ActualData[]>([]);
  const [overBudgetItems, setOverBudgetItems] = useState<{code: string, description: string, budgeted: number, actual: number, variance: number}[]>([]);
  
  // Fetch actual data from the spreadsheet
  useEffect(() => {
    const fetchActualsData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await axiosInstance.get<ActualData[]>('/api/sheet-data');
        setActualsData(response.data);
        
        // Process data after fetching
        processUnbudgetedExpenses(response.data, budget);
        processOverBudgetItems(response.data, budget);
      } catch (error) {
        console.error('Error fetching actuals data:', error);
        setError('Failed to load actuals data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
  
    fetchActualsData();
  }, [budget]);

  // Process and identify unbudgeted expenses
  const processUnbudgetedExpenses = (actuals: ActualData[], budget: GroupBudgetData) => {
    // Get all expense codes in the budget
    const budgetedExpenseCodes = budget.expenseLines.map(line => line.code);
    
    // Find actuals that are expenses and don't have a corresponding budget line
    const unbudgetedItems = actuals.filter(actual => {
      // Check if it's an expense (all expense codes start with numbers >= 100)
      const isExpense = /^\d+/.test(actual.code) && parseInt(actual.code.split('-')[0]) >= 100;
      return isExpense && !budgetedExpenseCodes.includes(actual.code) && actual.actual > 0;
    });
    
    setUnbudgetedExpenses(unbudgetedItems);
  };

  // Process and identify items that are over budget
  const processOverBudgetItems = (actuals: ActualData[], budget: GroupBudgetData) => {
    const overItems = [];
    
    for (const line of budget.expenseLines) {
      const actualItem = actuals.find(a => a.code === line.code);
      if (actualItem && actualItem.actual > 0) {
        const budgetedAmount = calculateBudgetedAmount(line);
        
        if (actualItem.actual > budgetedAmount) {
          overItems.push({
            code: line.code,
            description: actualItem.description || getDescriptionFromCode(line.code),
            budgeted: budgetedAmount,
            actual: actualItem.actual,
            variance: actualItem.actual - budgetedAmount
          });
        }
      }
    }
    
    // Sort by variance (highest overage first)
    setOverBudgetItems(overItems.sort((a, b) => b.variance - a.variance));
  };

  // Get description from IMC code
  const getDescriptionFromCode = (code: string): string => {
    const isIncome = parseInt(code) < 100;
    const codeInfo = isIncome 
      ? getIMCCodeByCode(code, IMC_INCOME_CODES)
      : getIMCCodeByCode(code, IMC_EXPENSE_CODES);
    
    return codeInfo ? codeInfo.label.replace(/^\d+ - /, '') : code;
  };

  // Calculate budgeted amount for a line
  const calculateBudgetedAmount = (line: GroupBudgetLine): number => {
    const quantity = line.quantity || 0;
    const value = line.value || 0;
    const multiplier = line.multiplier || 1;
    const frequency = line.frequency || '';
    const freqMult = getFrequencyMultiplier(frequency);
    
    return quantity * value * multiplier * freqMult;
  };

  // Get actual value for a given code
  const getActualValueByCode = (code: string): number => {
    const actualItem = actualsData.find(item => item.code === code);
    return actualItem?.actual || 0;
  };

  // Calculate budget vs actual summary for income
  const calculateIncomeSummary = (): BudgetVsActualSummary => {
    const budgetTotal = budget.incomeLines.reduce(
      (sum, line) => sum + (line.totalValue || calculateBudgetedAmount(line)),
      0
    );
    
    const actualTotal = budget.incomeLines.reduce(
      (sum, line) => sum + getActualValueByCode(line.code),
      0
    );
    
    // Forecast - prorated for the year based on current month
    const elapsedRatio = currentMonth / 12;
    const forecastTotal = actualTotal > 0 ? actualTotal / elapsedRatio : budgetTotal;
    
    return {
      budgetTotal,
      actualTotal,
      variance: actualTotal - budgetTotal,
      percentageSpent: budgetTotal > 0 ? (actualTotal / budgetTotal) * 100 : 0,
      forecastTotal,
      forecastVariance: forecastTotal - budgetTotal
    };
  };

  // Calculate budget vs actual summary for expenses
  const calculateExpenseSummary = (): BudgetVsActualSummary => {
    const budgetTotal = budget.expenseLines.reduce(
      (sum, line) => sum + (line.totalValue || calculateBudgetedAmount(line)),
      0
    );
    
    // Sum actual expenses including unbudgeted ones
    const actualTotal = actualsData
      .filter(item => /^\d+/.test(item.code) && parseInt(item.code.split('-')[0]) >= 100)
      .reduce((sum, item) => sum + item.actual, 0);
    
    // Forecast based on current month, with recurring expenses (administrative) projected linearly
    // and one-time expenses kept as is
    const elapsedRatio = currentMonth / 12;
    
    // Identify recurring expenses (administrative, salaries, etc.)
    const administrativeActual = actualsData
      .filter(item => {
        // Administrative typically starts with codes 3xx
        return /^3\d+/.test(item.code) && parseInt(item.code.split('-')[0]) >= 300;
      })
      .reduce((sum, item) => sum + item.actual, 0);
    
    // Project recurring expenses
    const projectedAdministrative = administrativeActual > 0 ? administrativeActual / elapsedRatio : 0;
    
    // Non-administrative actual expenses
    const nonAdminActual = actualTotal - administrativeActual;
    
    // Forecast with administrative projected and non-administrative taken as actual so far
    // plus remaining budgeted amount
    const forecastTotal = projectedAdministrative + nonAdminActual;
    
    return {
      budgetTotal,
      actualTotal,
      variance: actualTotal - budgetTotal,
      percentageSpent: budgetTotal > 0 ? (actualTotal / budgetTotal) * 100 : 0,
      forecastTotal,
      forecastVariance: forecastTotal - budgetTotal
    };
  };

  // Update cashflow projections based on the current bank balance
  const updateCashflowProjections = () => {
    const currentBalance = parseFloat(bankBalanceInput);
    
    if (isNaN(currentBalance)) {
      return;
    }
    
    const incomeSummary = calculateIncomeSummary();
    const expenseSummary = calculateExpenseSummary();
    
    // Remaining months in the year
    const remainingMonths = 12 - currentMonth;
    
    // Calculate monthly burn rate based on administrative expenses
    const administrativeMonthly = actualsData
      .filter(item => /^3\d+/.test(item.code) && parseInt(item.code.split('-')[0]) >= 300)
      .reduce((sum, item) => sum + item.actual, 0) / currentMonth;
    
    // Project end of month (assuming we're halfway through current month)
    const projectedEndOfMonth = currentBalance - (administrativeMonthly / 2);
    
    // Project end of year
    const projectedEndOfYear = currentBalance + 
      (incomeSummary.forecastTotal - incomeSummary.actualTotal) - 
      (expenseSummary.forecastTotal - expenseSummary.actualTotal);
    
    setCashflow({
      currentBankBalance: currentBalance,
      projectedEndOfMonth,
      projectedEndOfYear
    });
  };

  // Calculate percentage of total budget spent so far
  const calculateTotalPercentageSpent = (): number => {
    const totalBudget = calculateIncomeSummary().budgetTotal - calculateExpenseSummary().budgetTotal;
    const totalActual = calculateIncomeSummary().actualTotal - calculateExpenseSummary().actualTotal;
    
    return totalBudget !== 0 ? (totalActual / totalBudget) * 100 : 0;
  };

  // For charts: prepare budget vs actual data by category
  const getCategoryData = () => {
    // Income categories
    const incomeCategories: Record<string, { budget: number, actual: number }> = {};
    
    // Process budget income lines
    budget.incomeLines.forEach(line => {
      const code = line.code;
      const codeInfo = getIMCCodeByCode(code, IMC_INCOME_CODES);
      
      if (codeInfo) {
        const category = codeInfo.subCategories[0];
        const budgetValue = line.totalValue || calculateBudgetedAmount(line);
        
        if (!incomeCategories[category]) {
          incomeCategories[category] = { budget: 0, actual: 0 };
        }
        
        incomeCategories[category].budget += budgetValue;
      }
    });
    
    // Process actual income lines
    actualsData
      .filter(item => /^\d+/.test(item.code) && parseInt(item.code.split('-')[0]) < 100)
      .forEach(item => {
        const codeInfo = getIMCCodeByCode(item.code, IMC_INCOME_CODES);
        
        if (codeInfo) {
          const category = codeInfo.subCategories[0];
          
          if (!incomeCategories[category]) {
            incomeCategories[category] = { budget: 0, actual: 0 };
          }
          
          incomeCategories[category].actual += item.actual;
        }
      });
    
    // Expense categories
    const expenseCategories: Record<string, { budget: number, actual: number }> = {};
    
    // Process budget expense lines
    budget.expenseLines.forEach(line => {
      const code = line.code;
      const codeInfo = getIMCCodeByCode(code, IMC_EXPENSE_CODES);
      
      if (codeInfo) {
        const category = codeInfo.subCategories[0];
        const budgetValue = line.totalValue || calculateBudgetedAmount(line);
        
        if (!expenseCategories[category]) {
          expenseCategories[category] = { budget: 0, actual: 0 };
        }
        
        expenseCategories[category].budget += budgetValue;
      }
    });
    
    // Process actual expense lines
    actualsData
      .filter(item => /^\d+/.test(item.code) && parseInt(item.code.split('-')[0]) >= 100)
      .forEach(item => {
        const codeInfo = getIMCCodeByCode(item.code, IMC_EXPENSE_CODES);
        
        if (codeInfo) {
          const category = codeInfo.subCategories[0];
          
          if (!expenseCategories[category]) {
            expenseCategories[category] = { budget: 0, actual: 0 };
          }
          
          expenseCategories[category].actual += item.actual;
        }
      });
    
    // Format data for charts
    const incomeData = Object.entries(incomeCategories).map(([category, values]) => ({
      name: category.charAt(0).toUpperCase() + category.slice(1),
      budgeted: values.budget,
      actual: values.actual,
      type: 'Income'
    }));
    
    const expenseData = Object.entries(expenseCategories).map(([category, values]) => ({
      name: category.charAt(0).toUpperCase() + category.slice(1),
      budgeted: values.budget,
      actual: values.actual,
      type: 'Expense'
    }));
    
    return { incomeData, expenseData };
  };

  const incomeSummary = calculateIncomeSummary();
  const expenseSummary = calculateExpenseSummary();
  const { incomeData, expenseData } = getCategoryData();
  
  // Filter for top categories to show in the charts
  const topIncomeCategories = incomeData
    .sort((a, b) => b.budgeted - a.budgeted)
    .slice(0, 5);
    
  const topExpenseCategories = expenseData
    .sort((a, b) => b.budgeted - a.budgeted)
    .slice(0, 5);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Budget vs Actual - Financial Overview
      </Typography>
      
      {loading ? (
        <LinearProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {/* Progress toward budget year */}
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Budget Year Progress ({currentMonth}/12 months)
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(currentMonth / 12) * 100} 
                  sx={{ height: 10, borderRadius: 5, my: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {((currentMonth / 12) * 100).toFixed(0)}% of year elapsed
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={5}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 1 }}>
                  <TextField
                    label="Current Bank Balance"
                    value={bankBalanceInput}
                    onChange={(e) => setBankBalanceInput(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">€</InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    onClick={updateCashflowProjections}
                    sx={{ ml: 1 }}
                  >
                    Update
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Enter the current bank balance to see cash flow projections
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Net Budget Position
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    variant="h6" 
                    color={incomeSummary.budgetTotal - expenseSummary.budgetTotal >= 0 ? 'success.main' : 'error.main'}
                    sx={{ mr: 1 }}
                  >
                    €{(incomeSummary.budgetTotal - expenseSummary.budgetTotal).toLocaleString()}
                  </Typography>
                  {incomeSummary.budgetTotal - expenseSummary.budgetTotal >= 0 ? (
                    <TrendingUp color="success" />
                  ) : (
                    <TrendingDown color="error" />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Cashflow Cards */}
          {cashflow.currentBankBalance > 0 && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'column',
                    height: '100%',
                    bgcolor: 'primary.light',
                    color: 'white'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccountBalance sx={{ mr: 1 }} />
                    <Typography variant="h6">Current Bank Balance</Typography>
                  </Box>
                  <Typography variant="h4">
                    €{cashflow.currentBankBalance.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Updated manually
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  <Typography variant="h6">Projected End of Month</Typography>
                  <Typography 
                    variant="h4"
                    color={cashflow.projectedEndOfMonth >= 0 ? 'success.main' : 'error.main'}
                  >
                    €{cashflow.projectedEndOfMonth.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Based on current burn rate
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  <Typography variant="h6">Projected Year End</Typography>
                  <Typography 
                    variant="h4"
                    color={cashflow.projectedEndOfYear >= 0 ? 'success.main' : 'error.main'}
                  >
                    €{cashflow.projectedEndOfYear.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Based on forecasted income/expenses
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Main Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Income Summary */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Income Summary
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Grid container>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Budgeted</Typography>
                      <Typography variant="h6">€{incomeSummary.budgetTotal.toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Actual</Typography>
                      <Typography variant="h6">€{incomeSummary.actualTotal.toLocaleString()}</Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Percentage Received
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(incomeSummary.percentageSpent, 100)} 
                    color={incomeSummary.percentageSpent < (currentMonth / 12) * 100 ? "warning" : "success"}
                    sx={{ height: 8, borderRadius: 5, my: 1 }} 
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{incomeSummary.percentageSpent.toFixed(1)}%</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Target: {((currentMonth / 12) * 100).toFixed(0)}%
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Year-End Forecast
                  </Typography>
                  <Typography 
                    variant="h6" 
                    color={incomeSummary.forecastVariance >= 0 ? 'success.main' : 'error.main'}
                  >
                    €{incomeSummary.forecastTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={incomeSummary.forecastVariance >= 0 ? 'success.main' : 'error.main'}
                  >
                    {incomeSummary.forecastVariance >= 0 ? '+' : ''}
                    €{incomeSummary.forecastVariance.toLocaleString(undefined, { maximumFractionDigits: 0 })} vs budget
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            
            {/* Expense Summary */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom color="error">
                  Expense Summary
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Grid container>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Budgeted</Typography>
                      <Typography variant="h6">€{expenseSummary.budgetTotal.toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Actual</Typography>
                      <Typography variant="h6">€{expenseSummary.actualTotal.toLocaleString()}</Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Percentage Spent
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(expenseSummary.percentageSpent, 100)} 
                    color={expenseSummary.percentageSpent > (currentMonth / 12) * 100 ? "error" : "success"}
                    sx={{ height: 8, borderRadius: 5, my: 1 }} 
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{expenseSummary.percentageSpent.toFixed(1)}%</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Target: {((currentMonth / 12) * 100).toFixed(0)}%
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Year-End Forecast
                  </Typography>
                  <Typography 
                    variant="h6" 
                    color={expenseSummary.forecastVariance <= 0 ? 'success.main' : 'error.main'}
                  >
                    €{expenseSummary.forecastTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={expenseSummary.forecastVariance <= 0 ? 'success.main' : 'error.main'}
                  >
                    {expenseSummary.forecastVariance > 0 ? '+' : ''}
                    €{expenseSummary.forecastVariance.toLocaleString(undefined, { maximumFractionDigits: 0 })} vs budget
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Net Position & Health Assessment */}
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Financial Health Assessment
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Current Net Position
                  </Typography>
                  <Typography 
                    variant="h5" 
                    color={(incomeSummary.actualTotal - expenseSummary.actualTotal) >= 0 ? 'success.main' : 'error.main'}
                  >
                    €{(incomeSummary.actualTotal - expenseSummary.actualTotal).toLocaleString()}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Key Indicators
                </Typography>
                
                <Box 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  {expenseSummary.percentageSpent > (currentMonth / 12) * 100 ? (
                    <Warning color="warning" sx={{ mr: 1 }} />
                  ) : (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="body2">
                    Spending rate: {expenseSummary.percentageSpent > (currentMonth / 12) * 100 ? 'Above' : 'Below'} target
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  {incomeSummary.percentageSpent < (currentMonth / 12) * 100 ? (
                    <Warning color="warning" sx={{ mr: 1 }} />
                  ) : (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="body2">
                    Income rate: {incomeSummary.percentageSpent < (currentMonth / 12) * 100 ? 'Below' : 'Above'} target
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  {unbudgetedExpenses.length > 0 ? (
                    <Warning color="warning" sx={{ mr: 1 }} />
                  ) : (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="body2">
                    {unbudgetedExpenses.length} unbudgeted expense items
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {overBudgetItems.length > 0 ? (
                    <Warning color="warning" sx={{ mr: 1 }} />
                  ) : (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="body2">
                  {overBudgetItems.length > 0 ? overBudgetItems.length : 'No'} items over budget
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Alerts Section */}
          {(unbudgetedExpenses.length > 0 || overBudgetItems.length > 0) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Budget Alerts
              </Typography>
              
              <Grid container spacing={2}>
                {/* Unbudgeted Expenses */}
                {unbudgetedExpenses.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: 2, 
                        border: '1px solid', 
                        borderColor: 'warning.light',
                        bgcolor: 'warning.lighter'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Unbudgeted Expenses (€{unbudgetedExpenses.reduce((sum, item) => sum + item.actual, 0).toLocaleString()})
                      </Typography>
                      
                      <TableContainer component={Paper} sx={{ maxHeight: 200, overflow: 'auto' }}>
                        <Table size="small" aria-label="unbudgeted expenses table">
                          <TableHead>
                            <TableRow>
                              <TableCell>Code</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell align="right">Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {unbudgetedExpenses.map((expense) => (
                              <TableRow 
                                key={expense.code}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                              >
                                <TableCell component="th" scope="row">
                                  {expense.code}
                                </TableCell>
                                <TableCell>{expense.description}</TableCell>
                                <TableCell align="right">€{expense.actual.toLocaleString()}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                )}
                
                {/* Over Budget Items */}
                {overBudgetItems.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: 2, 
                        border: '1px solid', 
                        borderColor: 'error.light',
                        bgcolor: 'error.lighter'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Over Budget Items (€{overBudgetItems.reduce((sum, item) => sum + item.variance, 0).toLocaleString()} over)
                      </Typography>
                      
                      <TableContainer component={Paper} sx={{ maxHeight: 200, overflow: 'auto' }}>
                        <Table size="small" aria-label="over budget items table">
                          <TableHead>
                            <TableRow>
                              <TableCell>Code</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell align="right">Budgeted</TableCell>
                              <TableCell align="right">Actual</TableCell>
                              <TableCell align="right">Variance</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {overBudgetItems.map((item) => (
                              <TableRow 
                                key={item.code}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                              >
                                <TableCell component="th" scope="row">
                                  {item.code}
                                </TableCell>
                                <TableCell>{item.description}</TableCell>
                                <TableCell align="right">€{item.budgeted.toLocaleString()}</TableCell>
                                <TableCell align="right">€{item.actual.toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ color: 'error.main' }}>
                                  +€{item.variance.toLocaleString()}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Top Categories Charts */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Budget vs Actual by Category</Typography>
            
            <Grid container spacing={3}>
              {/* Top Income Categories */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Top Income Categories</Typography>
                  
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={topIncomeCategories}
                      margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45} 
                        textAnchor="end"
                        height={70}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        tickFormatter={(value) => `€${value / 1000}k`}
                      />
                      <Tooltip 
                        formatter={(value: number) => `€${value.toLocaleString()}`}
                        labelFormatter={(label) => `Category: ${label}`}
                      />
                      <Legend />
                      <Bar dataKey="budgeted" name="Budgeted Income" fill="#8884d8" />
                      <Bar dataKey="actual" name="Actual Income" fill="#4caf50" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
              
              {/* Top Expense Categories */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Top Expense Categories</Typography>
                  
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={topExpenseCategories}
                      margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45} 
                        textAnchor="end"
                        height={70}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        tickFormatter={(value) => `€${value / 1000}k`}
                      />
                      <Tooltip 
                        formatter={(value: number) => `€${value.toLocaleString()}`}
                        labelFormatter={(label) => `Category: ${label}`}
                      />
                      <Legend />
                      <Bar dataKey="budgeted" name="Budgeted Expense" fill="#ff9800" />
                      <Bar dataKey="actual" name="Actual Expense" fill="#f44336" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          </Box>
          
          {/* Forecasting and Recommendations */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Forecast Analysis & Recommendations
            </Typography>
            
            <Paper elevation={2} sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Financial Forecasting
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Year-End Income:</strong> €{incomeSummary.forecastTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      {" "}
                      ({incomeSummary.forecastVariance >= 0 ? '+' : ''}€{incomeSummary.forecastVariance.toLocaleString(undefined, { maximumFractionDigits: 0 })})
                    </Typography>
                    
                    <Typography variant="body2" gutterBottom>
                      <strong>Year-End Expenses:</strong> €{expenseSummary.forecastTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      {" "}
                      ({expenseSummary.forecastVariance >= 0 ? '+' : ''}€{expenseSummary.forecastVariance.toLocaleString(undefined, { maximumFractionDigits: 0 })})
                    </Typography>
                    
                    <Typography variant="body2" gutterBottom>
                      <strong>Projected Net Result:</strong>{" "}
                      <span style={{ 
                        color: (incomeSummary.forecastTotal - expenseSummary.forecastTotal) >= 0 
                          ? '#4caf50' 
                          : '#f44336'
                      }}>
                        €{(incomeSummary.forecastTotal - expenseSummary.forecastTotal).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                    </Typography>
                    
                    <Typography variant="body2" gutterBottom>
                      <strong>Burn Rate (Monthly):</strong>{" "}
                      €{(expenseSummary.actualTotal / currentMonth).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>
                    Forecast Methodology
                  </Typography>
                  
                  <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                    • Income forecasted based on actual trends and remaining budgeted items
                  </Typography>
                  
                  <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                    • Administrative expenses projected linearly based on current burn rate
                  </Typography>
                  
                  <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                    • Project-based expenses forecasted using scheduled events and historical patterns
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" color="secondary" gutterBottom>
                    Treasurer's Recommendations
                  </Typography>
                  
                  <Box sx={{ mb: 1 }}>
                    {unbudgetedExpenses.length > 0 && (
                      <Chip 
                        icon={<Warning />} 
                        label="Unbudgeted Expenses" 
                        color="warning" 
                        size="small" 
                        sx={{ mr: 1, mb: 1 }}
                      />
                    )}
                    
                    {overBudgetItems.length > 0 && (
                      <Chip 
                        icon={<Warning />} 
                        label="Over Budget Items" 
                        color="error" 
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    )}
                    
                    {expenseSummary.percentageSpent > (currentMonth / 12) * 100 && (
                      <Chip 
                        icon={<TrendingUp />} 
                        label="High Expense Rate" 
                        color="error" 
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    )}
                    
                    {incomeSummary.percentageSpent < (currentMonth / 12) * 100 && (
                      <Chip 
                        icon={<TrendingDown />} 
                        label="Low Income Rate" 
                        color="warning" 
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  </Box>
                  
                  <Typography variant="body2" paragraph>
                    <strong>Key Recommendations:</strong>
                  </Typography>
                  
                  {unbudgetedExpenses.length > 0 && (
                    <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                      • Review and allocate budget for €{unbudgetedExpenses.reduce((sum, item) => sum + item.actual, 0).toLocaleString()} in unbudgeted expenses
                    </Typography>
                  )}
                  
                  {overBudgetItems.length > 0 && (
                    <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                      • Address {overBudgetItems.length} items exceeding budget allocations by €{overBudgetItems.reduce((sum, item) => sum + item.variance, 0).toLocaleString()}
                    </Typography>
                  )}
                  
                  {expenseSummary.percentageSpent > (currentMonth / 12) * 100 && (
                    <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                      • Current spending pace is ahead of budget timeline - review expenses to ensure year-end targets can be met
                    </Typography>
                  )}
                  
                  {incomeSummary.percentageSpent < (currentMonth / 12) * 100 && (
                    <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                      • Income generation is behind schedule - review revenue streams and fundraising initiatives
                    </Typography>
                  )}
                  
                  {cashflow.projectedEndOfYear < 0 && (
                    <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                      • Projected negative year-end balance requires immediate attention to either reduce expenses or increase income
                    </Typography>
                  )}
                  
                  <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                    • Maintain minimum 3-month operating reserve (€{((expenseSummary.actualTotal / currentMonth) * 3).toLocaleString(undefined, { maximumFractionDigits: 0 })}) to ensure financial stability
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
};

export default BudgetVsActualDashboard;